import { Delete, Edit, HourglassBottom, Info, LockOpen } from '@mui/icons-material';
import { Box, IconButton, TablePaginationProps, TableSortLabel, Tooltip, TooltipProps, Typography, tooltipClasses } from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from "@mui/utils";
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getComparator, stableSort } from '../config/sorting';
import { useAuth } from '../hooks/useAuth';
import { ACTIONS, ICustomTableProps, Order } from '../interfaces/table';
import { CustomPagination } from './CustomPagination';

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.secondary.dark,
        color: theme.palette.common.white,
        boxShadow: theme.shadows[1],
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.contrastText,
        color: theme.palette.common.black,
        fontWeight: 500
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const CustomTable: FC<ICustomTableProps> = (props) => {
    const {
        headcells,
        onIconActionClicked,
        pagination,
        rows,
        hasCustomStyles,
        isEditPermitted,
        isDeletePermitted,
    } = props;

    const { t } = useTranslation();
    const { permissionsData, loginData } = useAuth();

    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof { [x: string]: any }>(headcells[0]?.label);
    const page = pagination && pagination.paginationProps ? (pagination.paginationProps as TablePaginationProps).page : 0;
    const rowsPerPage = pagination && pagination.paginationProps ? (pagination.paginationProps as TablePaginationProps).rowsPerPage : 5;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const visibleRows = useMemo(
        () => stableSort(rows, getComparator(order, orderBy)).slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        ),
        [order, orderBy, page, rowsPerPage, rows]
    );

    const handleRequestSort =
        (property: keyof { [x: string]: any }) =>
            (event: React.MouseEvent<unknown>) => {
                const isAsc = orderBy === property && order === "asc";
                setOrder(isAsc ? "desc" : "asc");
                setOrderBy(property);
            };

    const dynamicRender = (
        row: { [x: string]: any },
        i: number,
        index: number,
        column: string
    ) => {
        if (column === "actions" && onIconActionClicked && row.actions) {
            return row.actions.map(
                (action: { type: ACTIONS, message: string, disabled?: boolean }) => {
                    return (
                        <StyledTooltip
                            key={i + index + "-tooltip-" + action.type}
                            title={action.message}
                            placement="top"
                        >
                            <Box component="span">
                                <IconButton
                                    size="small"
                                    aria-label={action.type}
                                    sx={row.actions.length > 1 ? { mr: 0.5 } : {}}
                                    onClick={() => onIconActionClicked(action.type, row)}
                                    disabled={action.disabled}
                                >
                                    <Box sx={{ width: 24, height: 24 }}>
                                        {action.type === ACTIONS.EDIT && ((typeof isEditPermitted === "boolean" && isEditPermitted) || !isEditPermitted) && <Edit color={action.disabled ? "disabled" : "primary"} />}
                                        {action.type === ACTIONS.DELETE && ((typeof isDeletePermitted === "boolean" && isEditPermitted) || !isDeletePermitted) && <Delete color={action.disabled ? "disabled" : "error"} />}
                                        {action.type === ACTIONS.INFO && <Info color={action.disabled ? "disabled" : "secondary"} />}
                                        {action.type === ACTIONS.PASSWORD && <LockOpen color={action.disabled ? "disabled" : "secondary"} />}
                                        {action.type === ACTIONS.PENDING && <HourglassBottom color={action.disabled ? "disabled" : "info"} />}
                                    </Box>
                                </IconButton>
                            </Box>
                        </StyledTooltip>
                    );
                }
            );
        }

        if (hasCustomStyles && row[column] && column !== "name") {
            if (row[column] !== t("permissionsPage.permissionsForm.yesOption") && row[column] !== t("permissionsPage.permissionsForm.noOption")) {
                row[column] = <Typography variant="body2" sx={{ fontWeight: 600, fontStyle: 'italic' }}>{row[column]}</Typography>
            }
            else if (row[column] === t("permissionsPage.permissionsForm.yesOption")) {
                row[column] = <Typography variant="body2" sx={{ fontWeight: 600 }}>{row[column]}</Typography>
            }
        }

        return row[column] ? row[column] : "-";
    };

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} size='small' aria-label="customized table">
                <TableHead>
                    <TableRow>
                        {
                            headcells.map(
                                headcell => <StyledTableCell align={headcell.key === "actions" ? "right" : "left"} color="secondary" sx={{ py: 1 }} key={headcell.key}>
                                    {
                                        headcell.key !== "actions" ? <TableSortLabel
                                            active={orderBy === headcell.key}
                                            direction={orderBy === headcell.key ? order : "asc"}
                                            onClick={handleRequestSort(headcell.key)}
                                        >
                                            {t(headcell.label)}
                                            {
                                                orderBy === headcell.key ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {order === "desc"
                                                            ? "sorted descending"
                                                            : "sorted ascending"}
                                                    </Box>
                                                ) : null
                                            }
                                        </TableSortLabel> : <Box sx={{ mr: 5 }}>
                                            {t(headcell.label)}
                                        </Box>
                                    }
                                </StyledTableCell>
                            )
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        visibleRows.map((row, i) => (
                            <StyledTableRow key={row.id + i}>
                                {
                                    headcells.map(
                                        (headcell, index) => <StyledTableCell key={row.id + i + "-" + headcell + index} sx={headcell.key === "actions" ? { display: 'flex', justifyContent: "end" } : {}}>
                                            {
                                                dynamicRender(row, i, index, headcell.key)
                                            }
                                        </StyledTableCell>
                                    )
                                }
                            </StyledTableRow>
                        ))
                    }
                    {
                        rows.length === 0 && <TableRow>
                            <TableCell colSpan={12} sx={{ textAlign: "center", py: 1 }}>
                                {t("table.noRowsLabel")}
                            </TableCell>
                        </TableRow>
                    }
                    {emptyRows > 0 && (
                        <TableRow
                            style={{
                                height: 33 * emptyRows,
                            }}
                        >
                            <TableCell colSpan={6} />
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {
                pagination && visibleRows.length > 0 && (
                    <CustomPagination
                        type="table"
                        paginationProps={{
                            ...(pagination.paginationProps as TablePaginationProps),
                            rowsPerPageOptions: [5, 10, 25],
                            component: "div",
                        }}
                    />
                )
            }
        </TableContainer>
    );
}

export default CustomTable;
